import { html, type TemplateResult } from 'lit-html';
import { api, get } from '../api.js';
import { act, app, closeModal, confirmDialog, emptyRow, isAdmin, isOwner, openModal } from '../app.js';
import type { ConnectorTab } from '../connector-ui.js';
import { pill, when } from '../format.js';
import type { ApiKey, Connector } from '../types.js';

/**
 * Forwarding tab (any connector that declares events): where the connector passes status changes on to,
 * e.g. MoEngage, so flows can react to "Delivered" or "Returned".
 */
interface EventDef {
  id: string;
  label: string;
  description: string;
}
interface Destination {
  id: string;
  name: string;
  enabled: boolean;
  kind: 'moengage' | 'webhook';
  events: string[];
  api_key_ids: string[];
  moengage?: { data_center: string; workspace_id: string; event_name: string };
  webhook?: { url: string; auth_header: string };
  credential_set: boolean;
  version: number;
  updated_at: string;
  updated_by: string;
}
interface Delivery {
  id: string;
  event: string;
  ref_id: string;
  state: 'pending' | 'sending' | 'delivered' | 'failed';
  attempts: number;
  created_at: string;
  delivered_at?: string;
  last_status?: number;
  last_error?: string;
}

let connector = '';
let events: EventDef[] = [];
let destinations: Destination[] = [];
let keys: ApiKey[] = [];
let open: { id: string; deliveries: Delivery[] | null } | null = null;
const testResult = new Map<string, string>();

async function load(name: string): Promise<void> {
  connector = name;
  const [f, k] = await Promise.all([get<{ events: EventDef[]; destinations: Destination[] }>(`/forwarding/${name}`), get<{ keys: ApiKey[] }>('/keys')]);
  events = f.events;
  destinations = f.destinations;
  keys = k.keys;
}
const reload = () => load(connector);

const keyName = (id: string) => keys.find((k) => k.id === id)?.name ?? id;
const eventLabel = (id: string) => events.find((e) => e.id === id)?.label ?? id;
const sampleName = (pattern: string, e: EventDef) => pattern.replaceAll('{label}', e.label).replaceAll('{event}', e.id);

function destinationForm(existing: Destination | null): () => TemplateResult {
  const d = {
    name: existing?.name ?? 'MoEngage',
    enabled: existing?.enabled ?? true,
    kind: existing?.kind ?? ('moengage' as Destination['kind']),
    events: new Set(existing ? (existing.events.length ? existing.events : events.map((e) => e.id)) : events.map((e) => e.id)),
    keys: new Set(existing?.api_key_ids ?? keys.filter((k) => k.status === 'active' && /moengage/i.test(k.name)).map((k) => k.id)),
    data_center: existing?.moengage?.data_center ?? '01',
    workspace_id: existing?.moengage?.workspace_id ?? '',
    event_name: existing?.moengage?.event_name ?? 'Stannp Mail {label}',
    url: existing?.webhook?.url ?? '',
    auth_header: existing?.webhook?.auth_header ?? 'Authorization',
  };
  const submit = async (e: Event) => {
    e.preventDefault();
    const allEvents = d.events.size === events.length;
    const body = {
      name: d.name,
      enabled: d.enabled,
      kind: d.kind,
      events: allEvents ? [] : [...d.events],
      api_key_ids: [...d.keys],
      ...(d.kind === 'moengage'
        ? { moengage: { data_center: d.data_center, workspace_id: d.workspace_id.trim(), event_name: d.event_name.trim() } }
        : { webhook: { url: d.url.trim(), auth_header: d.auth_header.trim() } }),
      ...(existing ? { version: existing.version } : {}),
    };
    const ok = await act(
      () => (existing ? api('PUT', `/forwarding/${connector}/${existing.id}`, body) : api('POST', `/forwarding/${connector}`, body)),
      existing ? 'Destination saved' : 'Destination added. Now set its credential.',
      reload,
    );
    if (ok) closeModal();
  };
  const toggle = (set: Set<string>, id: string, on: boolean) => {
    if (on) set.add(id);
    else set.delete(id);
    app.rerender();
  };
  return () => html`<div class="modal-backdrop" @click=${(e: Event) => e.target === e.currentTarget && closeModal()}>
    <div class="modal wide" role="dialog" aria-modal="true" aria-labelledby="dest-title">
      <h2 id="dest-title">${existing ? `Edit “${existing.name}”` : 'Add a destination'}</h2>
      <form class="form-stack" @submit=${submit}>
        <label class="field">Name<input required maxlength="80" .value=${d.name} @input=${(e: Event) => (d.name = (e.target as HTMLInputElement).value)}></label>
        <div class="radios">
          <label class="radio"><input type="radio" name="kind" .checked=${d.kind === 'moengage'} @change=${() => { d.kind = 'moengage'; app.rerender(); }}>
            <b>MoEngage</b><span class="muted">Each status becomes a custom event on the person (customer_id = ref_id), for flows and segments.</span></label>
          <label class="radio"><input type="radio" name="kind" .checked=${d.kind === 'webhook'} @change=${() => { d.kind = 'webhook'; app.rerender(); }}>
            <b>Webhook</b><span class="muted">A JSON POST to any HTTPS URL, for other systems.</span></label>
        </div>
        ${d.kind === 'moengage'
          ? html`<div class="form-row">
                <label class="field">Data center<select @change=${(e: Event) => (d.data_center = (e.target as HTMLSelectElement).value)}>
                  ${['01', '02', '03', '04', '05', '06'].map((dc) => html`<option value=${dc} ?selected=${d.data_center === dc}>${dc} (api-${dc}.moengage.com)</option>`)}</select></label>
                <label class="field">Workspace ID<input required .value=${d.workspace_id} placeholder="MoEngage › Settings › Account › APIs" @input=${(e: Event) => (d.workspace_id = (e.target as HTMLInputElement).value)}></label>
              </div>
              <label class="field">Event name in MoEngage<input required maxlength="100" .value=${d.event_name} @input=${(e: Event) => { d.event_name = (e.target as HTMLInputElement).value; app.rerender(); }}></label>
              <p class="hint">{label} becomes the status, e.g. <b>${sampleName(d.event_name, events.find((e) => e.id === 'delivered') ?? events[0]!)}</b>.
                Use a fixed name such as “Stannp Mail Status” to get one event with a <code>status</code> attribute instead.</p>`
          : html`<label class="field">URL<input required type="url" placeholder="https://…" .value=${d.url} @input=${(e: Event) => (d.url = (e.target as HTMLInputElement).value)}></label>
              <label class="field">Header for the credential<input .value=${d.auth_header} @input=${(e: Event) => (d.auth_header = (e.target as HTMLInputElement).value)}></label>`}
        <div><span class="label">Events</span>
          <div class="actions-row no-margin mb-4"><button type="button" class="btn quiet compact" @click=${() => { events.forEach((e) => d.events.add(e.id)); app.rerender(); }}>All</button>
            <button type="button" class="btn quiet compact" @click=${() => { d.events.clear(); app.rerender(); }}>None</button></div>
          <div class="scopes">${events.map((ev) => html`<label><input type="checkbox" .checked=${d.events.has(ev.id)} @change=${(e: Event) => toggle(d.events, ev.id, (e.target as HTMLInputElement).checked)}>
            <b>${ev.label}</b><span class="muted">${ev.description}</span></label>`)}</div>
        </div>
        <div><span class="label">Only mail sent with these API keys</span>
          <div class="scopes">${keys.filter((k) => k.status === 'active' || d.keys.has(k.id)).map((k) => html`<label><input type="checkbox" .checked=${d.keys.has(k.id)} @change=${(e: Event) => toggle(d.keys, k.id, (e.target as HTMLInputElement).checked)}>
            <b>${k.name}</b><span class="muted">${k.mode === 'test' ? 'Test key' : 'Live key'} · ${k.preview}</span></label>`)}</div>
          <p class="hint">${d.keys.size ? 'Events are forwarded only for mail those keys asked for.' : html`<b>None ticked: events for mail from any key are forwarded.</b>`}</p>
        </div>
        <label class="check"><input type="checkbox" .checked=${d.enabled} @change=${(e: Event) => (d.enabled = (e.target as HTMLInputElement).checked)}> Forwarding on</label>
        <div class="actions"><button type="button" class="btn quiet" @click=${closeModal}>Cancel</button><button class="btn primary" ?disabled=${d.events.size === 0}>${existing ? 'Save' : 'Add destination'}</button></div>
      </form>
    </div></div>`;
}

async function toggleDeliveries(d: Destination): Promise<void> {
  if (open?.id === d.id) {
    open = null;
    return app.rerender();
  }
  open = { id: d.id, deliveries: null };
  app.rerender();
  const r = await act(() => get<{ deliveries: Delivery[] }>(`/forwarding/${connector}/${d.id}/deliveries`), null);
  if (open?.id === d.id) open.deliveries = r?.deliveries ?? [];
  app.rerender();
}

const DELIVERY_TONE = { pending: 'info', sending: 'info', delivered: 'positive', failed: 'critical' } as const;

function destinationCard(d: Destination): TemplateResult {
  const admin = isAdmin();
  const saveCredential = async (e: Event) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const value = (form.elements.namedItem('value') as HTMLInputElement).value;
    if (await act(() => api('PUT', `/forwarding/${connector}/${d.id}/credential`, { value }), 'Credential saved', reload)) form.reset();
  };
  const sendTest = async (e: Event) => {
    e.preventDefault();
    const v = Object.fromEntries(new FormData(e.target as HTMLFormElement)) as Record<string, string>;
    const r = await act(() => api<{ ok: boolean; status: number; error: string | null }>('POST', `/forwarding/${connector}/${d.id}/test`, { ref_id: v.ref_id, event: v.event }), null);
    if (r) testResult.set(d.id, r.ok ? `Delivered (HTTP ${r.status}).` : `Failed: ${r.error}`);
    app.rerender();
  };
  const remove = async () => {
    if (await confirmDialog({ title: `Delete “${d.name}”?`, body: 'Events stop being forwarded to it, and its stored credential is removed.', confirmLabel: 'Delete', tone: 'danger' })) {
      await act(() => api('DELETE', `/forwarding/${connector}/${d.id}`), 'Destination deleted', reload);
    }
  };
  const target = d.kind === 'moengage' ? `MoEngage workspace ${d.moengage?.workspace_id} (DC ${d.moengage?.data_center})` : d.webhook?.url;
  return html`<div class="card mt-4">
    <div class="row-between"><h2>${d.name}</h2><span>${d.enabled ? pill('On', 'positive') : pill('Off')} ${d.credential_set ? pill('Credential set', 'positive') : pill('No credential', 'warning')}</span></div>
    <dl class="kv mt-4">
      <dt>Sends to</dt><dd>${target}</dd>
      ${d.kind === 'moengage' ? html`<dt>Event name</dt><dd><code>${d.moengage?.event_name}</code></dd>` : ''}
      <dt>Events</dt><dd>${d.events.length ? d.events.map(eventLabel).join(', ') : `All ${events.length}`}</dd>
      <dt>For mail from</dt><dd>${d.api_key_ids.length ? d.api_key_ids.map(keyName).join(', ') : html`<b>any API key</b>`}</dd>
      <dt>Last changed</dt><dd>${when(d.updated_at)} by ${d.updated_by}</dd>
    </dl>
    ${admin ? html`<div class="actions-row"><button class="btn compact" @click=${() => openModal(destinationForm(d))}>Edit</button>
      <button class="btn quiet compact" @click=${() => toggleDeliveries(d)}>${open?.id === d.id ? 'Hide deliveries' : 'Recent deliveries'}</button>
      <button class="btn destructive outline compact" @click=${remove}>Delete</button></div>`
      : html`<div class="actions-row"><button class="btn quiet compact" @click=${() => toggleDeliveries(d)}>${open?.id === d.id ? 'Hide deliveries' : 'Recent deliveries'}</button></div>`}
    ${isOwner()
      ? html`<div class="secret-row mt-4"><div class="what"><b>${d.kind === 'moengage' ? 'MoEngage Data API key' : 'Credential'}</b>
            <p>${d.kind === 'moengage' ? 'MoEngage › Settings › Account › APIs › Data API key. Sent as Basic auth with the workspace ID.' : `Sent in the ${d.webhook?.auth_header} header.`} Never shown again.</p></div>
          <form @submit=${saveCredential}><input name="value" type="password" autocomplete="off" required placeholder=${d.credential_set ? 'Replace…' : 'Paste value'} aria-label="Credential"><button class="btn">${d.credential_set ? 'Replace' : 'Save'}</button></form></div>`
      : ''}
    ${admin
      ? html`<form class="filters mt-4" @submit=${sendTest}>
          <label>Test event<select name="event">${events.map((e) => html`<option value=${e.id} ?selected=${e.id === 'delivered'}>${e.label}</option>`)}</select></label>
          <label>For ref_id (a test person)<input name="ref_id" required placeholder="your own MoEngage test user's ID"></label>
          <button class="btn">Send test event</button>
        </form>
        <p class="hint">Sends one sample event right now, with <code>test: true</code>. MoEngage attaches it to that customer_id (and creates the user if it doesn't exist), so use a test user.</p>
        ${testResult.get(d.id) ? html`<div class="banner ${testResult.get(d.id)!.startsWith('Delivered') ? 'info' : 'critical'}">${testResult.get(d.id)}</div>` : ''}`
      : ''}
    ${open?.id === d.id
      ? open.deliveries === null
        ? html`<div class="loading"><span class="spinner" aria-hidden="true"></span>Loading…</div>`
        : html`<div class="table-wrap mt-4"><table>
            <thead><tr><th>Queued</th><th>Event</th><th>ref_id</th><th>Result</th></tr></thead>
            <tbody>${open.deliveries.length
              ? open.deliveries.map((x) => html`<tr><td class="nowrap">${when(x.created_at)}</td><td>${eventLabel(x.event)}</td><td><code class="id">${x.ref_id}</code></td>
                  <td>${pill(x.state, DELIVERY_TONE[x.state])} ${x.attempts > 1 ? html`<span class="muted">${x.attempts} attempts</span>` : ''}${x.last_error ? html`<br><span class="muted">${x.last_error}</span>` : ''}</td></tr>`)
              : emptyRow(4, 'Nothing forwarded yet.')}</tbody></table></div>`
      : ''}
  </div>`;
}

export const forwardingTab: ConnectorTab = {
  id: 'forwarding',
  label: 'Forwarding',
  load: async () => undefined,
  view: (c: Connector) => html`
    <p class="hint">Pass ${c.display_name}'s status updates on to other systems, e.g. back into MoEngage as a custom event on the person, so a flow can react
      when a letter is delivered or returned. Events carry IDs, template and status only, never names or addresses. They go out within a couple of minutes, with retries.</p>
    ${isAdmin() ? html`<div class="actions-row no-margin"><button class="btn primary compact" @click=${() => openModal(destinationForm(null))}>Add a destination</button></div>` : ''}
    ${destinations.length ? destinations.map(destinationCard) : html`<div class="card mt-4"><p class="muted">No destinations yet.</p></div>`}`,
};

/** The tab loads by connector name (the connectors page calls this before rendering). */
export const loadForwarding = load;
